import { Box, CircularProgress, styled, Typography } from "@material-ui/core";
import type { FC } from "react";
import { Navigate } from "react-router-dom";
import {
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import {
  selectActiveProgram,
  useMyPrograms,
} from "../../_lib/use-active-program";
import { linkToEpocusSettings } from "../../links";
import { BackToSettingsLink } from "../_lib/back-to-settings-link";
import { SettingsCard, SettingsCardLabel } from "../_lib/settings-card";
import { useCanManageProgramUsers } from "../_lib/use-can-manage-program-users";
import { AuditEventRow } from "./_lib/audit-event-row";
import { useEpocusProgramAuditTrailQuery } from "./_lib/audit-trail-operations";

const TRAIL_LIMIT = 100;

const PageRoot = styled(Box)({
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 720,
  margin: "0 auto",
  [epocusTheme.breakpoints.up("sm")]: {
    padding: 24,
  },
});

const LoadingRow = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const EventList = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

const Note: FC<{ children: string }> = ({ children }) => (
  <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

export const EpocusAuditTrailPage: FC = () => {
  const canManage = useCanManageProgramUsers();
  const { programs, loading: programsLoading } = useMyPrograms();
  const program = selectActiveProgram(programs);
  const programId = program?.id ?? null;

  const { data, loading, error } = useEpocusProgramAuditTrailQuery(
    { programId: programId ?? "", limit: TRAIL_LIMIT },
    { skip: !programId },
  );

  if (!canManage) {
    return <Navigate to={linkToEpocusSettings()} replace />;
  }

  const trail = data?.epocusProgramAuditTrail;

  return (
    <PageRoot>
      <BackToSettingsLink />
      <Box>
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            fontSize: rem(24),
            lineHeight: 1.2,
          }}
        >
          Activity log
        </Typography>
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_TEXT_SECONDARY,
            marginTop: 4,
          }}
        >
          What has been done to {program?.name ?? "your program"}, and by whom
        </Typography>
      </Box>

      {!trail && (programsLoading || loading) ? (
        <LoadingRow>
          <CircularProgress aria-label="Loading the activity log" />
        </LoadingRow>
      ) : !programId ? (
        <SettingsCard>
          <Note>Couldn't determine your program.</Note>
        </SettingsCard>
      ) : !trail && error ? (
        <SettingsCard>
          <Note>Couldn't load the activity log.</Note>
        </SettingsCard>
      ) : (
        <SettingsCard>
          <SettingsCardLabel>Recent activity</SettingsCardLabel>

          {trail?.enabled === false ? (
            <Note>
              Activity recording is turned off in this environment, so nothing
              new is being added. Anything already recorded is shown below.
            </Note>
          ) : (
            <Note>
              Every change made through the console or the setup assistant,
              newest first. Actions the assistant took name the admin who
              authorised it.
            </Note>
          )}

          {trail && trail.events.length === 0 ? (
            <Note>Nothing recorded for this program yet.</Note>
          ) : (
            <EventList>
              {trail?.events.map((event) => (
                <AuditEventRow key={event.id} event={event} />
              ))}
            </EventList>
          )}
        </SettingsCard>
      )}
    </PageRoot>
  );
};
